/**
 * SEOV2 component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 * See: https://www.gatsbyjs.com/tutorial/seo-and-social-sharing-cards-tutorial/
 */

import React, { FunctionComponent, useEffect } from 'react';
import Helmet from 'react-helmet';
import { globalHistory } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';

import { userCentrics } from '@savebetter/tracking-events';

import { useBankProducts } from '../context/BankProductsContext';
import { replaceCustomVars } from '../utils/replaceCustomVar';

import { MediaProps } from './Media';

interface SEOProps {
  title: string;
  description?: string;
  lang?: string;
  meta?: any[];
  disableIndexing?: boolean;
  image: MediaProps[];
  canonical?: string;
}

export const SEOv2: FunctionComponent<React.PropsWithChildren<SEOProps>> = ({
  description = '',
  lang = 'en',
  image,
  meta = [],
  title,
  canonical,
  disableIndexing = false
}) => {
  const { contextProducts } = useBankProducts();
  const {
    site: { siteMetadata, pathPrefix }
  } = useStaticQuery(
    graphql`
      query {
        site {
          pathPrefix
          siteMetadata {
            defaultTitle
            titleTemplate
          }
        }
      }
    `
  );

  useEffect(() => {
    userCentrics.initializeTracking();
  }, []);

  const isMobileApp = typeof window !== 'undefined' && localStorage.getItem('mobileApp') === 'true';
  const currentUrl = globalHistory.location.href;
  const website = globalHistory.location.origin + pathPrefix + '/';
  const shortDescription = description ? description.split('.')[0] : '';

  return (
    <Helmet
      htmlAttributes={{
        lang
      }}
      title={replaceCustomVars(title, contextProducts)}
      defaultTitle={replaceCustomVars(siteMetadata.defaultTitle, contextProducts)}
      titleTemplate={replaceCustomVars(siteMetadata.titleTemplate, contextProducts)}
      link={canonical ? [{ rel: 'canonical', key: canonical, href: canonical }] : []}
      meta={[
        {
          name: 'url',
          content: currentUrl
        },
        {
          property: 'title',
          content: replaceCustomVars(title, contextProducts)
        },
        {
          name: 'description',
          content: replaceCustomVars(shortDescription, contextProducts)
        },
        {
          property: 'og:url',
          content: currentUrl
        },
        {
          property: 'og:site_name',
          content: website
        },

        {
          property: 'og:title',
          content: replaceCustomVars(title, contextProducts)
        },
        {
          property: 'og:description',
          content: replaceCustomVars(shortDescription, contextProducts)
        },
        {
          property: 'og:type',
          content: 'blog'
        },
        { name: 'twitter:card', content: 'blog' },
        { name: 'twitter:site', content: website },
        {
          name: 'twitter:title',
          content: title
        },
        {
          name: 'twitter:description',
          content: replaceCustomVars(shortDescription, contextProducts)
        },
        {
          name: 'facebook-domain-verification',
          content: 'c79tmd3sv6c38bwfwcqh3ielkeoed1'
        }
      ]
        .concat(
          image?.[0]?.img
            ? [
                {
                  property: 'og:image',
                  content: image[0].img
                },
                {
                  property: 'og:image:width',
                  content: '382'
                },
                {
                  property: 'og:image:height',
                  content: '200'
                },
                { name: 'twitter:image', content: image[0].img }
              ]
            : [
                { name: 'twitter:image:alt', content: 'image' },
                { name: 'og:image:alt', content: 'image' }
              ]
        )
        .concat(
          [
            {
              property: 'og:image',
              content: 'https://a.storyblok.com/f/220361/420x120/10d5642387/raisin-logo-420-x-120.png'
            },
            {
              name: 'twitter:image',
              content: 'https://a.storyblok.com/f/220361/420x120/10d5642387/raisin-logo-420-x-120.png'
            }
          ],
          meta
        )
        .concat(
          disableIndexing
            ? [{ name: 'robots', content: 'noindex max-image-preview:large' }]
            : [{ name: 'robots', content: 'max-image-preview:large' }]
        )}
    >
      {!isMobileApp && (
        <script
          defer
          id="ze-snippet"
          src="https://static.zdassets.com/ekr/snippet.js?key=d4d2151f-6268-499b-965d-e44a7da24a67"
        />
      )}
    </Helmet>
  );
};
